import apiClient from './apiClient';

/**
 * 文件管理服务
 */
class FileService {
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }

    /**
     * 列出文件
     */
    async listFiles(containerId, path = '/') {
        try {
            const response = await apiClient.get(`${this.baseUrl}/files/list`, {
                params: { container_id: containerId, path }
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 读取文件内容
     */
    async readFile(containerId, path) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/files/read`, {
                params: { container_id: containerId, path }
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 写入文件
     */
    async writeFile(containerId, path, content) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/write`, {
                container_id: containerId,
                path,
                content
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 上传文件
     */
    async uploadFile(containerId, path, file) {
        try {
            const formData = new FormData();
            formData.append('container_id', containerId);
            formData.append('path', path);
            formData.append('file', file);

            const response = await apiClient.post(`${this.baseUrl}/files/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 下载文件
     */
    async downloadFile(containerId, path) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/files/download`, {
                params: { container_id: containerId, path },
                responseType: 'blob'
            });

            // 创建下载链接
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', path.split('/').pop());
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 删除文件
     */
    async deleteFile(containerId, path) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/delete`, {
                container_id: containerId,
                path
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 复制文件
     */
    async copyFile(containerId, srcPath, dstPath) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/copy`, {
                container_id: containerId,
                src_path: srcPath,
                dst_path: dstPath
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 移动文件
     */
    async moveFile(containerId, srcPath, dstPath) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/move`, {
                container_id: containerId,
                src_path: srcPath,
                dst_path: dstPath
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 创建目录
     */
    async createDirectory(containerId, path) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/mkdir`, {
                container_id: containerId,
                path
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 压缩文件
     */
    async compressFile(containerId, srcPath, dstPath, format = 'tar.gz') {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/compress`, {
                container_id: containerId,
                src_path: srcPath,
                dst_path: dstPath,
                format
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 解压文件
     */
    async extractFile(containerId, srcPath, dstPath) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/extract`, {
                container_id: containerId,
                src_path: srcPath,
                dst_path: dstPath
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 异步压缩文件
     */
    async compressFileAsync(containerId, srcPath, dstPath, format = 'tar.gz') {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/compress-async`, {
                container_id: containerId,
                src_path: srcPath,
                dst_path: dstPath,
                format
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 异步解压文件
     */
    async extractFileAsync(containerId, srcPath, dstPath) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/extract-async`, {
                container_id: containerId,
                src_path: srcPath,
                dst_path: dstPath
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 远程下载文件
     */
    async remoteDownload(containerId, url, dstPath, async = true) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/remote-download`, {
                container_id: containerId,
                url,
                dst_path: dstPath,
                async
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取任务状态
     */
    async getTaskStatus(containerId, taskId) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/tasks/status`, {
                params: { container_id: containerId, task_id: taskId }
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 获取任务列表
     */
    async getTaskList(containerId) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/tasks/list`, {
                params: { container_id: containerId }
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 修改文件权限
     */
    async chmod(containerId, path, mode, recursive = false) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/chmod`, {
                container_id: containerId,
                path,
                mode,
                recursive
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 修改文件所有者
     */
    async chown(containerId, path, owner, group = null, recursive = false) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/files/chown`, {
                container_id: containerId,
                path,
                owner,
                group,
                recursive
            });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }
}

// 导出管理员和用户端实例
export const adminFileService = new FileService('/api/admin/host');
export const userFileService = new FileService('/api/user/host');

export default FileService;
